import { css } from 'astroturf';
import React from 'react';
import CatalogCard from './_catalogCardLayout';
import CollectionInfoDialog from './_collectionInfoDialog';
import { CollectionIcon } from './_collectionIcon';

const styles = css`
  .categories {
    position: relative;
    display: flex;
    gap: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    margin: 0.25rem 0 0.25rem -0.5rem;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 2rem;
      height: 100%;
      background: linear-gradient(to left, #fff, #fff0);
      pointer-events: none;
    }
  }

  .category {
    font-size: 10px;
    border-radius: 100px;
    padding: 0.1rem 0.55rem;
    background: #00aeff21;
    color: #333;
  }

  .cardHeader {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    h4 {
      margin: 0;
      line-height: 1.5;
      color: #0a1530;
      min-width: 0;
      display: block;

      small {
        font-size: 0.75em;
        color: #39728d;
        font-weight: 400;
      }
    }

    p {
      margin: 0;
      line-height: 1.25;
      font-size: 0.875em;
      text-wrap: pretty;

      &.author {
        margin-top: 0.5rem;
        opacity: 0.925;
        font-size: 0.75rem;

        b {
          font-weight: 500;
          color: #39728d;
        }
      }
    }
  }

  .nameButton {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
    display: inline;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
    }
  }

  .add {
    margin-left: auto;
    flex: 0 0 2.3rem;
    min-width: 2.3rem;
    background: #0a153011;
    color: #39728d;
    font-size: 2rem;
    line-height: 1;
    padding: 0;
    border: 0;
    border-radius: 4px;
    transition:
      background 0.15s ease-in-out,
      color 0.15s ease-in-out;

    &:hover,
    &:focus-visible {
      background: #0a1530;
      color: #fff;
    }
  }
`;

const CollectionCard = React.memo(
  ({ collection, onDownload, onAddToCollection, normalizeType, activeSnippet = null, activeCollection = null }) => (
    <CatalogCard
      onDownload={() => onDownload(collection)}
      type="collection"
      jsonData={collection}
      extraActions={
        <button
          className={styles.add}
          type="button"
          aria-label="Edit collection for export"
          data-tip="Edit collection for export"
          onClick={() => onAddToCollection(collection)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-pencil"
          >
            <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4" />
            <path d="M13.5 6.5l4 4" />
          </svg>
        </button>
      }
    >
      <CollectionIcon />
      <div className={styles.cardHeader}>
        <CollectionInfoDialog
          collection={collection}
          normalizeType={normalizeType}
          onDownload={onDownload}
          activeSnippet={activeSnippet}
          activeCollection={activeCollection}
          renderTrigger={(show) => (
            <h4>
              <button type="button" className={styles.nameButton} onClick={show} data-tip="Show collection contents">
                {collection.name} <small>({collection.snippets.length})</small>
              </button>
            </h4>
          )}
        />
        {!!collection.categories.length && (
          <div className={styles.categories}>
            {collection.categories.map((category) => (
              <span key={category} className={styles.category}>
                {category}
              </span>
            ))}
          </div>
        )}
        {collection.description && <p>{collection.description}</p>}
        {collection.author && (
          <p className={styles.author}>
            By{' '}
            {collection.authorUrl ? (
              <a href={collection.authorUrl} target="_blank" rel="noopener noreferrer">
                <b>{collection.author}</b>
              </a>
            ) : (
              <b>{collection.author}</b>
            )}
          </p>
        )}
      </div>
    </CatalogCard>
  )
);

export default CollectionCard;
